import React, { Fragment, useEffect, useRef, useState } from 'react';
import { GENDER_OPTIONS } from '@features/onboarding/constants';
import { FieldArrayWithId, UseFormReturn, Path } from 'react-hook-form';
import { DependentSchema } from '@features/onboarding/schema/onboarding';
import { ApplicantFormDatepicker } from '@features/onboarding/components/2-applicants-form/ApplicantFormDatepicker';

import {
  FormField,
  FormItem,
  FormControl,
  FormMessageSpan,
} from '@components/ui/form';
import { Separator } from '@components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio-group';
import { Label } from '@components/ui/label';
import { FloatingInput } from '@components/ui/input-floating';
import {
  DropdownFloating,
  DropdownFloatingSearch,
} from '@components/ui/dropdown-floating';
import { useUnderReviewStore } from 'src/store/under-review';
import { useAcceptanceChangeStore } from 'src/store/acceptance-change';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useApplicationStore } from 'src/store/application';
import { cn } from '@utils/index';
import { formatPhone, validatePhone } from '@utils/phone';
import { validateZipcode, getStateCodeByZipcode } from '@utils/zipcode';
import { lookupCityStateByZip } from '@utils/zipLookup';
import {
  ONBOARDING_ZIP_LS_KEY as ZIP_LS_KEY,
  ONBOARDING_PHONE_LS_KEY as PHONE_LS_KEY,
} from '@constants/index';

export const HOUSEHOLD_INCOME_OPTIONS = [
  { value: 'Under $25,000', label: 'Under $25,000' },
  { value: '$25,000–$34,999', label: '$25,000–$34,999' },
  { value: '$35,000–$49,999', label: '$35,000–$49,999' },
  { value: '$50,000–$74,999', label: '$50,000–$74,999' },
  { value: '$75,000–$99,999', label: '$75,000–$99,999' },
  { value: '$100,000–$149,999', label: '$100,000–$149,999' },
  { value: '$150,000–$199,999', label: '$150,000–$199,999' },
  { value: '$200,000–$299,999', label: '$200,000–$299,999' },
  { value: '$300,000+', label: '$300,000+' },
];

interface DependentFormItemProps {
  field: FieldArrayWithId<DependentSchema, 'applicants', 'id'>;
  index: number;
  applicant: string;
  form: UseFormReturn<DependentSchema>;
  totalDependents: number;
  dependentNumber?: number;
  remove: (index: number) => void;
  scrollRef?: (el: HTMLDivElement | null) => void;
  showContactErrors?: boolean;
}

export const ApplicantFormItem = ({
  field,
  index,
  form,
  totalDependents,
  dependentNumber,
  remove,
  scrollRef,
  showContactErrors,
}: DependentFormItemProps) => {
  const { isUnderReview, sessionLockedForHumanReview } = useUnderReviewStore();
  const { isApplicationAccepted } = useAcceptanceChangeStore();

  // Zip + phone for the primary applicant. Kept in local state and written to
  // the onboarding store directly with skipFormReset, so the react-hook-form
  // form (and its validation/navigation) is never touched.
  const [zipValue, setZipValue] = useState<string>(
    () => useOnboardingStore.getState().data?.address?.zipcode || ''
  );
  const [phoneValue, setPhoneValue] = useState<string>(() =>
    formatPhone(useOnboardingStore.getState().data?.phone || '')
  );

  const clearPregnancyAnswer = () => {
    const currentData = useOnboardingStore.getState().data;
    useOnboardingStore.getState().setData(
      {
        clearPregnancyOnNextPHQ: true,
        healthQuestions: {
          ...currentData.healthQuestions,
          initialQuestions: {
            ...currentData.healthQuestions?.initialQuestions,
            pregnancy: undefined,
          },
        },
      },
      { skipFormReset: true }
    );
  };

  // Keep the inputs in sync when the store gets populated after mount
  // (initial application load / returning to this step).
  // The application-level address (zip-only) doesn't always round-trip from the
  // backend, so back zip/phone with localStorage. Self-healing sync: if the
  // store value is missing but localStorage has one, restore it into the store
  // (so the gate sees it) and the input. Keeps Next unblocked across reloads.
  const storeZip = useOnboardingStore((s) => s.data?.address?.zipcode || '');
  const storePhone = useOnboardingStore((s) => s.data?.phone || '');
  // The loaded application (current.payload) holds the authoritative values,
  // which don't always make it into the onboarding data — read them directly.
  const appZip = useApplicationStore(
    (s) =>
      (s.applications?.[0]?.address as { zipcode?: string } | undefined)
        ?.zipcode || ''
  );
  const appPhone = useApplicationStore(
    (s) =>
      s.applications?.[0]?.applicants?.find((a) => a.applicant === 1)?.phone ||
      ''
  );
  useEffect(() => {
    if (field.applicant !== 'primary') return;
    let val = storeZip || appZip || localStorage.getItem(ZIP_LS_KEY) || '';
    if (val && val !== storeZip) {
      // Restore into the onboarding store so the Next gate / save can see it.
      const cur = useOnboardingStore.getState().data;
      useOnboardingStore.getState().setData(
        { address: { ...(cur.address || {}), zipcode: val } },
        { skipFormReset: true }
      );
    }
    setZipValue((prev) => (prev === val ? prev : val));
  }, [storeZip, appZip, field.applicant]);
  useEffect(() => {
    if (field.applicant !== 'primary') return;
    const raw =
      storePhone || appPhone || localStorage.getItem(PHONE_LS_KEY) || '';
    if (raw && raw !== storePhone) {
      useOnboardingStore
        .getState()
        .setData({ phone: raw }, { skipFormReset: true });
    }
    const formatted = formatPhone(raw);
    setPhoneValue((prev) => (prev === formatted ? prev : formatted));
  }, [storePhone, appPhone, field.applicant]);

  const handleZipChange = async (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 5);
    setZipValue(digits);
    localStorage.setItem(ZIP_LS_KEY, digits);

    if (digits.length < 5) {
      const current = useOnboardingStore.getState().data;
      useOnboardingStore.getState().setData(
        { address: { ...(current.address || {}), zipcode: digits } },
        { skipFormReset: true }
      );
      return;
    }

    // Full zip -> always refresh city & state. State is derived instantly from
    // the zip; city comes from the lookup (cleared meanwhile to drop stale data).
    const state = getStateCodeByZipcode(digits) || '';
    const current = useOnboardingStore.getState().data;
    useOnboardingStore.getState().setData(
      {
        address: { ...(current.address || {}), zipcode: digits, state, city: '' },
      },
      { skipFormReset: true }
    );

    const lookup = await lookupCityStateByZip(digits);
    const latest = useOnboardingStore.getState().data;
    // Skip if the zip changed again while the lookup was in flight.
    if (latest.address?.zipcode === digits && lookup) {
      useOnboardingStore.getState().setData(
        {
          address: {
            ...(latest.address || {}),
            city: lookup.city || '',
            state: lookup.state || state,
            country: lookup.country || latest.address?.country || 'US',
          },
        },
        { skipFormReset: true }
      );
    }
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhone(value);
    setPhoneValue(formatted);
    localStorage.setItem(PHONE_LS_KEY, formatted);
    useOnboardingStore
      .getState()
      .setData({ phone: formatted }, { skipFormReset: true });
  };

  const zipInvalid = !!showContactErrors && !validateZipcode(zipValue);
  const phoneInvalid = !!showContactErrors && !validatePhone(phoneValue);
  const zipErrorMessage = !zipValue
    ? 'Zip code is required'
    : 'Please enter a valid US zip code';
  const phoneErrorMessage = !phoneValue
    ? 'Phone number is required'
    : 'Please enter a valid phone number';

  const handleDateChange = (key: 'month' | 'day' | 'year', value: string) => {
    form.setValue(
      `applicants.${index}.dateOfBirth.${key}` as Path<DependentSchema>,
      value
    );
  };

  const formatDependentName = (applicant: string) =>
    applicant?.[0]?.toUpperCase() + applicant?.slice(1);
  const isYou = field.applicant === 'primary';
  const allowTodayDob = field.applicant === 'dependent';

  let applicantLabel = '';
  if (isYou) {
    applicantLabel = 'Myself';
  } else if (field.applicant === 'spouse') {
    applicantLabel = 'Spouse';
  } else if (field.applicant === 'dependent') {
    applicantLabel =
      dependentNumber && dependentNumber > 1
        ? `Dependent ${dependentNumber}`
        : 'Dependent';
  } else {
    applicantLabel = formatDependentName(field.applicant);
  }
  const sanitizeTextInput = (value: string) =>
    value
      .replace(/[^A-Za-zéñüóíáúèàùëïöäüÿçñ\s\-'\.]/g, '')
      .replace(/<[^>]*>/gi, '')
      .replace(/script|javascript/gi, '')
      .replace(/\s+/g, ' ');

  const parseWeight = (val: string): number | '' => {
    const numericVal = val.replace(/\D/g, '').slice(0, 4);
    if (numericVal === '') return '';
    return parseInt(numericVal, 10);
  };

  return (
    <Fragment key={field.id}>
      {!isYou && <Separator className="my-5 md:my-7" />}
      <div ref={scrollRef} className="max-w-[968px] relative">
        {totalDependents > 1 && !isYou && (
          <button
            className="size-8 absolute top-2 -right-2 cursor-pointer flex items-center justify-center"
            onClick={(e) => {
              e.preventDefault();
              remove(index);
            }}
          >
            <div className="relative xl:size-5 size-6 flex items-center justify-center transition-transform duration-200 rotate-45">
              <span className="absolute w-full rounded-lg h-1 bg-primary" />
              <span className="absolute w-1 h-full rounded-lg bg-primary" />
            </div>
          </button>
        )}

        <p className="text-black text-4xl font-bold max-md:text-2xl">
          {applicantLabel}
        </p>

        <div className="mt-5 flex flex-col md:flex-row md:items-center justify-between gap-6.5 max-md:gap-[15px]">
          <FormField
            control={form.control}
            name={`applicants.${index}.firstName`}
            render={({ field }) => (
              <FormItem className="flex flex-col w-full max-w-80 mb-auto">
                <FormControl>
                  <FloatingInput
                    id={`firstName-${index}`}
                    label="First name"
                    ref={field.ref}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(sanitizeTextInput(e.target.value));
                      form.clearErrors(`applicants.${index}.firstName`);
                    }}
                    wrapperClass="max-w-auto"
                    inputClass="h-13.5 xl:!h-16.5 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3 max-md:text-[18px]"
                  />
                </FormControl>
                {form.formState.errors?.applicants?.[index]?.firstName
                  ?.message && (
                  <FormMessageSpan
                    className="text-center mt-2"
                    text={
                      form.formState.errors?.applicants?.[index]?.firstName
                        ?.message
                    }
                  />
                )}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={`applicants.${index}.midName`}
            render={({ field }) => (
              <FormItem className="mb-auto flex flex-col w-full max-w-80">
                <FormControl>
                  <FloatingInput
                    id={`midName-${index}`}
                    label="Middle name"
                    ref={field.ref}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(sanitizeTextInput(e.target.value));
                      form.clearErrors(`applicants.${index}.midName`);
                    }}
                    wrapperClass="max-w-auto"
                    inputClass="h-13.5 xl:!h-16.5 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3"
                  />
                </FormControl>
                {form.formState.errors?.applicants?.[index]?.midName
                  ?.message && (
                  <FormMessageSpan
                    className="text-center mt-2"
                    text={
                      form.formState.errors?.applicants?.[index]?.midName
                        ?.message
                    }
                  />
                )}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={`applicants.${index}.lastName`}
            render={({ field }) => (
              <FormItem className="mb-auto flex flex-col w-full max-w-80">
                <FormControl>
                  <FloatingInput
                    id={`lastName-${index}`}
                    label="Last name"
                    ref={field.ref}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(sanitizeTextInput(e.target.value));
                      form.clearErrors(`applicants.${index}.lastName`);
                    }}
                    wrapperClass="max-w-auto"
                    inputClass="h-13.5 xl:!h-16.5 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3"
                  />
                </FormControl>
                {form.formState.errors?.applicants?.[index]?.lastName
                  ?.message && (
                  <FormMessageSpan
                    className="text-center mt-2"
                    text={
                      form.formState.errors?.applicants?.[index]?.lastName
                        ?.message
                    }
                  />
                )}
              </FormItem>
            )}
          />
        </div>

        <div className="mt-5 flex flex-col md:flex-row md:items-center justify-between gap-6.5 max-md:gap-[15px]">
          <FormField
            control={form.control}
            name={`applicants.${index}.gender`}
            render={({ field: { ref, ...field } }) => (
              <FormItem className="flex flex-col w-43 mb-auto">
                <FormControl>
                  <DropdownFloating
                    id={`gender-${index}`}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      if (e === 'female') {
                        clearPregnancyAnswer();
                      }
                      form.clearErrors(`applicants.${index}.gender`);
                    }}
                    options={GENDER_OPTIONS}
                    label="Gender"
                    error={!!form.formState.errors?.applicants?.[index]?.gender}
                    inputClass="border border-[#B7B7B7] text-[18px] rounded-lg w-full h-13.5 xl:h-16.5 md:max-w-[200px]"
                  />
                </FormControl>
                {form.formState.errors?.applicants?.[index]?.gender
                  ?.message && (
                  <FormMessageSpan
                    className="text-center mt-2"
                    text={
                      form.formState.errors?.applicants?.[index]?.gender
                        ?.message
                    }
                  />
                )}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={`applicants.${index}.dateOfBirth`}
            render={({ field: { ref, ...field } }) => (
              <FormItem
                className={cn(
                  'flex flex-col md:flex-row md:items-center justify-between gap-6.5 max-md:gap-2 w-full max-w-96 md:max-w-[540px] mb-auto',
                  (isUnderReview || isApplicationAccepted || sessionLockedForHumanReview) &&
                    'pointer-events-none'
                )}
              >
                <FormControl>
                  <ApplicantFormDatepicker
                    idxForm={index}
                    form={form}
                    month={field.value.month}
                    day={field.value.day}
                    year={field.value.year}
                    errors={
                      form.formState.errors?.applicants?.[index]?.dateOfBirth
                    }
                    onChange={handleDateChange}
                    allowToday={allowTodayDob}
                  />
                </FormControl>
                {form.formState.errors?.applicants?.[index]?.dateOfBirth
                  ?.message && (
                  <FormMessageSpan
                    className="text-center mt-2"
                    text={
                      form.formState.errors?.applicants?.[index]?.dateOfBirth
                        ?.message
                    }
                  />
                )}
              </FormItem>
            )}
          />
        </div>
        {isYou && (
          <div className="mt-4.5 flex max-md:flex-col gap-3 max-md:gap-8.5">
            <div className="mb-0 flex flex-col w-full lg:max-w-62 max-md:max-w-[85%]">
              <FloatingInput
                id="zipcode"
                label="Zip code"
                type="text"
                value={zipValue}
                onChange={(e) => handleZipChange(e.target.value)}
                maxLength={5}
                aria-invalid={zipInvalid}
                wrapperClass="max-w-auto"
                inputClass="h-13.5 xl:!h-16.5 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3 max-md:text-[18px]"
              />
              {zipInvalid && (
                <FormMessageSpan
                  className="text-center mt-2"
                  text={zipErrorMessage}
                />
              )}
            </div>
            <FormField
              control={form.control}
              name={'householdIncome' as Path<DependentSchema>}
              render={({ field: { ref, value, onChange } }) => (
                <FormItem className="mb-0 flex flex-col w-full lg:max-w-62 max-md:max-w-[85%]">
                  <FormControl>
                    <DropdownFloatingSearch
                      id={`householdIncome-${index}`}
                      label="Total household income"
                      ref={ref}
                      value={value as unknown as string | undefined}
                      onChange={(selectedValue) => {
                        form.clearErrors(
                          'householdIncome' as Path<DependentSchema>
                        );
                        onChange(selectedValue as unknown as string);
                      }}
                      options={HOUSEHOLD_INCOME_OPTIONS}
                      error={!!form.formState.errors?.householdIncome}
                      wrapperClass="max-w-auto"
                      inputClass="h-13.5 xl:!h-16.5 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3 max-md:text-[18px]"
                    />
                  </FormControl>
                  {form.formState.errors?.householdIncome?.message && (
                    <FormMessageSpan
                      className="text-center mt-2"
                      text={form.formState.errors?.householdIncome?.message}
                    />
                  )}
                </FormItem>
              )}
            />
            <div className="mb-0 flex flex-col w-full lg:max-w-62 max-md:max-w-[85%]">
              <FloatingInput
                id={`phone-${index}`}
                label="Phone number"
                type="tel"
                value={phoneValue}
                onChange={(e) => handlePhoneChange(e.target.value)}
                maxLength={14}
                placeholder="(###) ###-####"
                aria-invalid={phoneInvalid}
                wrapperClass="max-w-auto"
                inputClass="h-13.5 xl:!h-16.5 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3 max-md:text-[18px]"
              />
              {phoneInvalid && (
                <FormMessageSpan
                  className="text-center mt-2"
                  text={phoneErrorMessage}
                />
              )}
            </div>
          </div>
        )}
        <Separator className="xl:my-7.5 my-4 max-md:hidden" />
        <div
          className={cn(
            'border-gray flex flex-col md:flex-row gap-5 w-full flex-1 mt-5 md:mt-0',
            (isUnderReview || isApplicationAccepted || sessionLockedForHumanReview) && 'pointer-events-none'
          )}
        >
          <div className="w-full flex flex-col max-md:flex-row gap-5 max-md:items-center max-md:mt-5">
            <p className="flex items-center max-md:h-13.5 mb-auto gap-5 xl:text-xl text-base font-normal max-md:text-xl">
              <img
                src="/images/height.svg"
                alt="height"
                className="xl:size-11 size-7 shrink-0 max-md:hidden"
              />
              Height
            </p>

            <div className="flex gap-3 w-full h-full">
              <FormField
                control={form.control}
                name={`applicants.${index}.height.ft`}
                render={({ field }) => (
                  <FormItem className="w-full mb-auto max-md:max-w-25">
                    <FormControl>
                      <FloatingInput
                        id={`height-${index}`}
                        value={Number.isNaN(field.value) ? '' : field.value}
                        label="Ft"
                        type="number"
                        inputClass="h-13.5 xl:!h-16.5 min-h-auto w-full outline-none border !text-[18px] border-[#B7B7B7] p-2 rounded-lg"
                        onChange={(e) => {
                          let val = e.target.value;
                          if (val.length > 2) {
                            val = val.slice(0, 2);
                          }
                          if (val === '') {
                            field.onChange('');
                            return;
                          }
                          const num = parseInt(val, 10);
                          if (isNaN(num) || num < 0 || num > 7) {
                            return;
                          }
                          field.onChange(num);
                        }}
                        min={1}
                        max={7}
                        aria-invalid={
                          !!form.formState.errors?.applicants?.[index]?.height
                            ?.ft
                        }
                      />
                    </FormControl>
                    {form.formState.errors?.applicants?.[index]?.height?.ft
                      ?.message && (
                      <FormMessageSpan
                        className="text-center mt-2"
                        text={
                          form.formState.errors?.applicants?.[index]?.height?.ft
                            ?.message
                        }
                      />
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`applicants.${index}.height.in`}
                render={({ field }) => (
                  <FormItem className="w-full mb-auto max-md:max-w-25">
                    <FormControl>
                      <FloatingInput
                        id={`height-${index}-2`}
                        value={Number.isNaN(field.value) ? '' : field.value}
                        label="In"
                        type="number"
                        inputClass="h-13.5 xl:!h-16.5 min-h-auto w-full outline-none border !text-[18px] border-[#B7B7B7] p-2 rounded-lg"
                        onChange={(e) => {
                          let val = e.target.value;
                          if (val.length > 2) {
                            val = val.slice(0, 2);
                          }
                          if (val === '') {
                            field.onChange('');
                            return;
                          }
                          const num = parseInt(val, 10);
                          if (isNaN(num) || num < 0 || num > 11) {
                            return;
                          }
                          field.onChange(num);
                        }}
                        min={0}
                        max={11}
                        aria-invalid={
                          !!form.formState.errors?.applicants?.[index]?.height
                            ?.in
                        }
                      />
                    </FormControl>
                    {form.formState.errors?.applicants?.[index]?.height?.in
                      ?.message && (
                      <FormMessageSpan
                        className="text-center mt-2"
                        text={
                          form.formState.errors?.applicants?.[index]?.height?.in
                            ?.message
                        }
                      />
                    )}
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex w-full md:border-l md:border-r md:px-8 box-border">
            <div className="w-full md:w-auto flex flex-row md:flex-col gap-5">
              <p className="flex items-center gap-5 xl:text-xl text-base max-md:h-13.5 mb-auto font-normal max-md:mb-0 max-md:text-xl">
                <img
                  src="/images/weight.svg"
                  alt="weight"
                  className="xl:size-11 size-7 shrink-0 max-md:hidden"
                />
                Weight
              </p>

              <FormField
                control={form.control}
                name={`applicants.${index}.weight`}
                render={({ field }) => (
                  <FormItem className="mb-0 flex flex-col w-full h-full max-w-[206px] sm:max-w-full">
                    <FormControl>
                      <FloatingInput
                        id={`weight-${index}-2`}
                        label="Lbs"
                        value={Number.isNaN(field.value) ? '' : field.value}
                        inputClass="h-13.5 xl:!h-16.5 min-h-auto !w-full !text-[18px] outline-none border border-[#B7B7B7] p-2 rounded-lg"
                        type="number"
                        onChange={(e) => {
                          const parsed = parseWeight(e.target.value);
                          field.onChange(parsed);
                          form.clearErrors(`applicants.${index}.weight`);
                        }}
                        aria-invalid={
                          !!form.formState.errors?.applicants?.[index]?.weight
                        }
                      />
                    </FormControl>
                    {form.formState.errors?.applicants?.[index]?.weight
                      ?.message && (
                      <FormMessageSpan
                        className="text-center mt-2"
                        text={
                          form.formState.errors?.applicants?.[index]?.weight
                            ?.message
                        }
                      />
                    )}
                  </FormItem>
                )}
              />
            </div>
          </div>
          <div className="flex w-full">
            <div className="flex flex-col max-md:flex-row max-md:items-center max-md:gap-10">
              <p className="mb-8 flex items-center gap-5 xl:text-xl text-base font-normal max-md:mb-0 max-md:text-xl">
                <img
                  src="/images/nicotine.svg"
                  alt="nicotine"
                  className="xl:size-11 size-7 shrink-0 max-md:hidden"
                />
                Use nicotine?
              </p>
              <FormField
                control={form.control}
                name={`applicants.${index}.nicotine`}
                render={({ field: { ref, ...field } }) => (
                  <FormItem className="mb-0 flex flex-col">
                    <FormControl>
                      <RadioGroup
                        onValueChange={(value) => {
                          // field.onChange(value === 'yes');
                          form.setValue(
                            `applicants.${index}.nicotine`,
                            value === 'yes',
                            {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            }
                          );
                        }}
                        value={field.value ? 'yes' : 'no'}
                        className="flex items-center gap-x-10"
                      >
                        <div className="flex items-center cursor-pointer">
                          <RadioGroupItem
                            value="no"
                            id={`not-use-nicotine-${index}`}
                          />
                          <Label
                            htmlFor={`not-use-nicotine-${index}`}
                            className="text-black text-2xl pl-3 md:pl-4 cursor-pointer uppercase max-lg:text-base"
                          >
                            No
                          </Label>
                        </div>
                        <div className="flex items-centercursor-pointer">
                          <RadioGroupItem
                            value="yes"
                            id={`use-nicotine-${index}`}
                          />
                          <Label
                            htmlFor={`use-nicotine-${index}`}
                            className="text-black text-2xl pl-3 md:pl-4 cursor-pointer uppercase max-lg:text-base"
                          >
                            Yes
                          </Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        {/* {index > 0 && index + 1 === totalDependents && (
          <Separator className="mt-5 md:mt-7" />
        )} */}
      </div>
    </Fragment>
  );
};
